<template>
    <div class="table-container">
        <table>
            <tbody>
                <tr v-for="(row, index) in rowData" :key="index">
                    <th>{{ row.label }}</th>
                    <td>
                        <span v-html="row.value" />
                        <tooltip v-if="row.showTooltip" :tooltip-text="row.tooltipText" />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import Tooltip from '@/components/features/Tooltip.vue';
import { pick } from 'lodash';
export default {
    components: {
        Tooltip
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        cardDataKeys: {
            type: Array,
            required: true
        }
    },
    computed: {
        rowData() {
            return pick(this.tableDataConfig, this.cardDataKeys);
        },
        tableDataConfig() {
            const cashbackLimitString = this.model.getCashbackLimitString(this.$translate('month_short', 'mån'), this.$translate('year_short', 'år'), this.$translate('unlimited_cashback', 'Obegränsad'));
            return {
                'annual_fee': {
                    label: this.$translate('annual_fee', 'Årsavgift'),
                    value: this.model.yearlyCostHtml,
                    showTooltip: this.model.hasFirstYearOffer,
                    tooltipText: this.$translate('annual_fee_offer_tooltip', '')
                },
                'max_credit': {
                    label: this.$translate('max_credit', 'Maxkredit'),
                    value: this.model.getMaxCreditString(this.$translate('unlimited_max_credit', 'Obegränsad')),
                },
                'effective_interest': {
                    label: this.$translate('effective_interest', 'Effektiv ränta'),
                    value: this.model.effectiveInterestString
                },
                'interest_free_days': {
                    label: this.$translate('interest_free', 'Räntefritt'),
                    value: this.model.getInterestFreeDaysString(this.$translate('days', '{days} dagar'))
                },
                'has_travel_insurance': {
                    label: this.$translate('travel_insurance', 'Reseförsäkring'),
                    value: `<i class="${this.$getBooleanIconClass(this.model.hasTravelInsurance)}" />`
                },
                'currency_exchange_fee': {
                    label: this.$translate('currency_exchange_fee', 'Valutakursavgift'),
                    value: this.model.getCurrencyExchangeFeeString()
                },
                'has_airline_bonus': {
                    label: this.$translate('airline_bonus', 'Flygbonus'),
                    value: `<i class="${this.$getBooleanIconClass(this.model.hasAirlineBonus)}" />`
                },
                'cash_withdrawal_fee': {
                    label: this.$translate('withdraw_fee', 'Kontantuttag'),
                    value: this.model.getWithdrawalFeeString()
                },
                'has_travel_discount': {
                    label: this.$translate('travel_discount', 'Reserabatt'),
                    value: `<i class="${this.$getBooleanIconClass(this.model.hasTravelDiscounts)}" />`
                },
                'cashback_percentage': {
                    label: this.$translate('cashback', 'Cashback'),
                    value: this.model.hasUnlimitedCashback 
                        ? this.$format.percentage(this.model.cashbackPercentage) 
                        : `${this.$format.percentage(this.model.cashbackPercentage)} (max ${cashbackLimitString})`
                },
                'bonus_percentage': {
                    label: this.$translate('bonus', 'Bonus'),
                    value: this.model.hasUnlimitedCashback 
                        ? this.$format.percentage(this.model.cashbackPercentage) 
                        : `${this.$format.percentage(this.model.cashbackPercentage)} (max ${cashbackLimitString})`
                },
                'bonus_limit': {
                    label: this.$translate('bonus_limit', 'Maximal bonus'),
                    value: cashbackLimitString
                },
                'cashback_limit': {
                    label: this.$translate('cashback_limit', 'Maximal cashback'),
                    value: cashbackLimitString
                },
                'shopping_cashback_limit': {
                    label: this.$translate('shopping_cashback_limit', 'Maximal cashback'),
                    value: cashbackLimitString
                },
                'discounts': {
                    label: this.$translate('discounts', 'Rabatter'),
                    value: this.model.hasDiscountProgram 
                        ? this.model.getDiscountString(
                            this.$translate('discount_in_stores', 'Upp till {percentage} i {count} butiker')
                        )
                        : this.$translate('no_discounts', 'Inga rabatter')
                },
                'fuel_chain_offers': {
                    label: this.$translate('fuel_chain_offers', 'Rabatt på drivmedelskedja'),
                    value: this.model.fuelOffersChain || '-'
                },
                'manned_stations_discount': {
                    label: this.$translate('manned_stations_discount', 'Rabatt på bemannade stationer'),
                    value: this.model.fuelDiscountManned
                        ? this.model.getFuelDiscountMannedString(
                            this.$translate('fuel_per_liter_discount', '{discount} öre / liter')
                        )
                        : this.$translate('no_discounts', 'Inga rabatter')
                },
                'unmanned_stations_discount': {
                    label: this.$translate('unmanned_stations_discount', 'Rabatt på obemannade stationer'),
                    value: this.model.fuelDiscountUnmanned
                        ? this.model.getFuelDiscountUnmannedString(
                            this.$translate('fuel_per_liter_discount', '{discount} öre / liter')
                        ) 
                        : this.$translate('no_discounts', 'Inga rabatter')
                },
                'has_wrong_fuel_insurance': {
                    label: this.$translate('wrong_fuel_insurance', 'Feltankningsförsäkring'),
                    value: `<i class="${this.$getBooleanIconClass(this.model.hasWrongFuelInsurance)}" />`
                }
            };
        }
    }
};
</script>

<style lang="scss" scoped>
    .table-container {
        overflow: auto;
        @include border-radius(15px);
        @include border(all,1px,$purplebg);
        box-shadow: $bxs;

        table {
            width: 100%;
            @include device(pad) {
                table-layout: fixed;
            }

            tr {
                @include border(bottom,1px,$purplebg);
                &:last-child { border: 0; }
                &:nth-child(even) {
                    background: $graybg;
                }

                th, td {
                    @include spacing(padding, 3);
                    @include font-size(16px,8px);
                    vertical-align: middle;

                    @include device(mobile) {
                        @include font-size(14px,8px);
                    }
                }
                th {
                    font-weight: bold;
                    @include border(right,1px,$purplebg);
                }
            }
        }
    }
    ::v-deep .standard-yearly-cost {
        display: inline;
        color: red;
        margin-left: 5px;
        text-decoration: line-through;
    }
</style>